import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  debounce,
  fetchData,
  formatDate,
  formatNumber,
  getEncryptedRoute,
  toasterrormsg,
  useMenuIdPermissions,
} from '../../../reusable'
import { KTIcon } from '../../../_metronic/helpers'
import { ToastContainer } from 'react-toastify'
import DataTable from 'react-data-table-component'
import Loader from '../components/Loader'
import { Tr } from '../../../_metronic/i18n/Tr'

const STATUS_BADGE: Record<string, { label: string; color: string }> = {
  DRAFT: { label: 'Draft', color: 'secondary' },
  UNDER_REVIEW: { label: 'Under Review', color: 'info' },
  APPROVED: { label: 'Approved', color: 'primary' },
  ACTIVATED: { label: 'Active', color: 'success' },
  REJECTED: { label: 'Rejected', color: 'danger' },
  COMPLETED: { label: 'Completed', color: 'dark' },
  PAUSED: { label: 'Paused', color: 'warning' },
}

const VendorSearchAdsList: FC = () => {
  const t = Tr()
  const navigate = useNavigate()
  const { urlData, menuId } = useMenuIdPermissions() as any
  const vendorId = urlData?.vendorId || ''
  const vendorName = urlData?.vendorName || ''

  const [sponserList, setSponserList] = useState<any[]>([])
  const [inputValue, setInputValue] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [totalRecords, setTotalRecords] = useState(0)
  const [limit, setLimit] = useState(10)
  const [offset, setOffset] = useState(0)
  const [sortField, setSortField] = useState('')
  const [sortOrder, setSortOrder] = useState('')
  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [focus, setFocus] = useState('')

  const fetchVendorSponsers = async () => {
    try {
      if (!isDataLoaded) setIsLoading(true)
      const param = {
        vendorId: parseInt(vendorId) || 0,
        limit,
        offset,
        term: searchTerm.trim(),
        sort: sortOrder,
        sortBy: sortField,
      }
      const response = await fetchData('admin/sponser/list', param)
      if (response.status == '200') {
        const data = response?.data ?? {}
        setTotalRecords(data.totalRecord || 0)
        setSponserList(data.list || [])
        setIsDataLoaded(true)
      } else {
        toasterrormsg(response?.message || 'An error occurred')
        setSponserList([])
        setTotalRecords(0)
      }
    } catch (error) {
      console.error('Error fetching vendor sponsers:', error)
      toasterrormsg('Failed to fetch vendor sponsers')
      setSponserList([])
      setTotalRecords(0)
    } finally {
      setIsDataLoaded(true)
      setIsLoading(false)
    }
  }

  // Search
  const sponserSearch = (text: string, isfoc: any) => {
    setInputValue(text)
    setFocus(isfoc)
    debouncedSearch(text)
  }

  const debouncedSearch = useRef(
    debounce((value: string) => {
      setSearchTerm(value)
      setOffset(0)
    }, 500)
  ).current

  useEffect(() => {
    return () => debouncedSearch.cancel()
  }, [])

  const handleSort = (column: any, sortDirection: string) => {
    try {
      const fieldName = column.selector ? String(column.selector).split('.').pop() : ''
      setSortField(fieldName || '')
      setSortOrder(sortDirection === 'asc' ? 'asc' : 'desc')
    } catch (error) {
      console.error('Error in sorting:', error)
    }
  }

  const handlePageChange = (newPage: number) => {
    const totalPages = Math.ceil(totalRecords / limit)
    if (newPage === 1) setOffset(0)
    else if (newPage === totalPages) setOffset((totalPages - 1) * limit)
    else setOffset((newPage - 1) * limit)
  }

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setLimit(newRowsPerPage)
    setOffset(0)
  }

  const CustomHeader = (props: any) => {
    const inputRef: any = useRef(null)
    const handleChange = async (e: any) => {
      await props.customSearch(e, 1)
    }
    useEffect(() => {
      if (inputRef.current && props.isfoc === 1) inputRef.current.focus()
    }, [props.isfoc])

    return (
      <div className='d-flex justify-content-between w-100 my-5'>
        <div className='d-flex align-items-center position-relative my-1'>
          <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
          <input
            type='text'
            data-kt-user-table-filter='search'
            className='form-control form-control-solid w-250px ps-14'
            placeholder={t('Search')}
            value={props.search}
            onChange={(e: any) => handleChange(e.target.value)}
            ref={inputRef}
          />
        </div>
      </div>
    )
  }

  const columns = useMemo(
    () => [
   
      {
        name: t('Property Name'),
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.en_parent_name ? row.en_parent_name : (row.en_name || '-')}</div>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.en_parent_name || row.en_name,
        width: '180px',
      },
      {
        name: t('Listing Name'),
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.en_parent_name ? (row.en_name || '-') : '-'}</div>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.en_parent_name ? row.en_name : '',
        width: '180px',
      },
      {
        name: t('Status'),
        cell: (row: any) => {
          const cfg = row.status && STATUS_BADGE[row.status] ? STATUS_BADGE[row.status] : { label: 'N/A', color: 'secondary' }
          return (
            <div className='my-3'>
              <span className={`badge badge-light-${cfg.color} fw-bold fs-7 px-3 py-2`}>{t(cfg.label)}</span>
            </div>
          )
        },
        sortable: true,
        selector: (row: any) => row.status,
        width: '140px',
      },
      {
        name: t('Duration'),
        cell: (row: any) => (
          <div className='my-3'>
<div className='fw-bold fs-5'>
  {row?.duration != null
    ? `${row.duration}${row?.durationType ? ` ${row.durationType}` : ''}`
    : '- -'}
</div>          </div>
        ),
        sortable: true,
        selector: (row: any) => row.duration,
        width: '130px',
      },
      {
        name: t('Amount'),
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.price ? formatNumber(parseFloat(row.price)) : '-'}</div>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.price,
        width: '150px',
      },
      {
        name: t('Start Date'),
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-6'>
              {row.startDate ? formatDate(row.startDate, 'DD-MM-YYYY') : <span className='text-muted'>-</span>}
            </div>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.startDate,
        width: '130px',
      },
      {
        name: t('End Date'),
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-6'>
              {row.endDate ? formatDate(row.endDate, 'DD-MM-YYYY') : <span className='text-muted'>-</span>}
            </div>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.endDate,
        width: '130px',
      },
      {
        name: t('Action'),
        cell: (row: any) => (
          <div className='my-3'>
            <Link
              to={getEncryptedRoute('../searchadvertisement/view/', { menuId, sponserId: row.sponserId })}
              className='btn btn-light-primary btn-sm d-flex align-items-center gap-1 px-3 py-2 rounded-pill'
              title='View Details'
            >
              <KTIcon iconName='eye' className='fs-4' iconType='solid' />
              <span className='fs-7'>{t('View')}</span>
            </Link>
          </div>
        ),
        sortable: false,
        width: '130px',
      },
    ],
    [t, menuId]
  )

  useEffect(() => {
    if (vendorId) fetchVendorSponsers()
  }, [limit, offset, sortField, sortOrder, searchTerm, vendorId])

  useEffect(() => {
    document.title = `Insayr | ${vendorName || 'Vendor'} Search Ads`
  }, [vendorName])

  return (
    <>
      <ToastContainer />
      {isLoading && <Loader />}

      {/* Header */}
      <div className='d-flex flex-wrap align-items-center gap-3 mb-4'>
        <button
          className='btn btn-light-danger btn-sm d-flex align-items-center gap-2'
          onClick={() => navigate(-1)}
        >
          <KTIcon iconName='arrow-left' className='fs-3' />
          {t('Back')}
        </button>
        <div>
          <h3 className='fw-bold mb-0 fs-4'>{vendorName || t('Vendor Search Ads')}</h3>
          <span className='text-muted fs-7'>{t('All search ads by this vendor')}</span>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={sponserList}
        pagination
        paginationServer
        onChangePage={handlePageChange}
        paginationTotalRows={totalRecords}
        paginationPerPage={limit}
        onChangeRowsPerPage={handleRowsPerPageChange}
        paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 30]}
        sortServer
        onSort={handleSort}
        subHeader
        subHeaderComponent={<CustomHeader search={inputValue} isfoc={focus} customSearch={sponserSearch} />}
        paginationComponentOptions={{ rowsPerPageText: t('Rows Per Page') }}
        noDataComponent={
          <div className='vs-empty-state'>
            <KTIcon iconName='document' className='fs-3x text-muted mb-3' />
            <div className='fw-bold fs-5 text-gray-600 mb-1'>{t('No Search Ads Found')}</div>
            <p className='text-muted fs-7'>{t('This vendor has no search ads yet.')}</p>
          </div>
        }
      />
    </>
  )
}

export { VendorSearchAdsList }
